import { useState } from 'react';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { THRESHOLD_UNITS, Timezones } from 'infrastructure/enums';

import { hypotensionSymptoms } from '../templates/spo2';
import { generateSpO2Message } from '../blurbs';
import { getNursingInterventionValues } from '../helpers';

import type { IUseSpo2Props } from '../types';
import type { ISpo2Values } from 'components/molecules/rpm-alert-assessment/types';

export const useSpoAssessment = ({
  activityId,
  activityDate,
  values,
  patientGuid,
  addComment,
  closeModal,
  handleSubmit,
}: IUseSpo2Props) => {
  const [openEmergencyModal, setOpenEmergencyModal] = useState<boolean>(false);
  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const initialValues: ISpo2Values = {
    // Q1
    patient: undefined,
    other: null,
    relationship: null,
    // Q2
    AOC: null,
    // Q3
    last: null,
    next: null,
    // Q4
    isOneMoreReading: null,
    bounds: null,
    repeatReadingWas: null,
    // Q5
    symptoms: null,
    confirmedSymptoms: [],
    deniedSymptoms: [],
    symptomsOnset: null,
    emergencyCare: null,
    checkSymptoms: true,
    // Q6
    isTakeMedications: null,
    prescribed: [],
    otherPrescribed: null,
    // Q7
    isTakePrnMedications: null,
    prnMedications: [],
    otherPrnMedications: null,
    // Q8
    wearO2Home: null,
    homeO2Setting: null,
    homeO2SettingValue: null,
    // Q9
    confirmedFactors: [],
    deniedFactors: [],
    // Q10
    nursingIntervention: [],
    notEscalated: [],
    interventionDetails: null,
    escalationToClinic: [],
    // Q11
    verbalized: null,
  };

  const reading = `${values.spo2} ${THRESHOLD_UNITS.SpO2}`;
  const timeZone =
    patientInfo?.loginInfo?.timezone || Timezones['America/Chicago'];

  const dataInPatientTimeZone = convertDateToTimeZone(
    new Date(Number(activityDate)),
    Timezones[timeZone],
  );
  const dateOfReading = DateFormatter({
    date: dataInPatientTimeZone,
  });

  const symptomsList = hypotensionSymptoms;

  const emergentSymptoms = symptomsList
    .filter((item) => item.required)
    .map((item) => item.label);

  const preparedData = (data: ISpo2Values) => {
    const medications = [...data.prescribed, data.otherPrescribed].filter(
      Boolean,
    );
    const prnMedications = [
      ...data?.prnMedications,
      data.otherPrnMedications,
    ].filter(Boolean);

    const isSpeakWithPatient = data.patient === 'Patient';
    const lastDate = data.last
      ? DateFormatter({ date: new Date(data.last) })
      : null;
    const nextDate = data.next
      ? DateFormatter({ date: new Date(data.next) })
      : null;
    const nursingInterventionValues = getNursingInterventionValues(
      data?.nursingIntervention,
    );
    const confirmedEmergentSymptoms = data.confirmedSymptoms?.filter(
      (item: string) => emergentSymptoms.includes(item),
    );
    const confirmedSymptomsWithoutEmergent = data.confirmedSymptoms?.filter(
      (item: string) => !emergentSymptoms.includes(item),
    );

    return {
      // Did you speak to patient?
      Q1: {
        reading,
        dateOfReading,
        patient: data.patient,
        relationship: data.relationship,
        speakWithPatient: isSpeakWithPatient,
        speakWithOther: data.other,
      },
      // Mental Status Assessment
      Q2: {
        AOC: data.AOC,
      },
      // Appointments
      Q3: {
        last: lastDate,
        next: nextDate,
      },
      //
      Q4: {
        isOneMoreReading: data.isOneMoreReading ?? null,
        bounds: data.bounds ?? null,
        repeatReadingWas: data.repeatReadingWas ?? null,
      },
      Q5: {
        confirmedSymptoms: confirmedSymptomsWithoutEmergent,
        deniedSymptoms: data.deniedSymptoms,
        isAllSymptomsDenied:
          data.deniedSymptoms?.length === symptomsList.length,
        confirmedEmergentSymptoms,
        symptomsOnset: data.symptomsOnset,
        emergencyModalAnswer: data.emergencyCare,
      },
      // Medications
      Q6: {
        isTakeMedications: data.isTakeMedications,
        medications,
        hasMedications: !!medications?.length,
      },
      // PRM medications
      Q7: {
        isTakePrnMedications: data.isTakePrnMedications,
        prnMedications,
        hasPrnMedications: !!prnMedications.length,
      },
      // Does patient wear home O2?
      Q8: {
        wearO2Home: data.wearO2Home ?? null,
        homeO2Setting: data.homeO2Setting,
        homeO2SettingValue: data.homeO2SettingValue,
      },
      // Factors Assessment
      Q9: {
        confirmedFactors: data.confirmedFactors,
        deniedFactors: data.deniedFactors,
      },
      // Nursing Intervention
      Q10: {
        ...nursingInterventionValues,
        notEscalated: data.notEscalated,
        interventionDetails: data.interventionDetails,
        escalationToClinic: data.escalationToClinic,
      },
      Q11: {
        verbalized: data.verbalized ?? null,
      },
    };
  };

  const toggleEmergencyModal = () => setOpenEmergencyModal((prev) => !prev);

  const onSaveForm = (formData: ISpo2Values) => {
    const preparedValues = preparedData(formData);
    const message = generateSpO2Message(preparedValues);

    handleSubmit(message);
    addComment(activityId);
    closeModal();
  };

  return {
    initialValues,
    openEmergencyModal,
    symptomsList,
    onSaveForm,
    toggleEmergencyModal,
  };
};
